import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { guestPosts, type GuestPost } from "@/data/guestPosts";

const buildMarkdown = (g: GuestPost) =>
  `# ${g.h1}\n\n${g.intro}\n\n${g.content}`;

const buildPlainText = (g: GuestPost) => {
  const md = buildMarkdown(g);
  return md
    .split("\n")
    .map((l) => {
      if (l.startsWith("### ")) return l.slice(4);
      if (l.startsWith("## ")) return l.slice(3);
      if (l.startsWith("# ")) return l.slice(2);
      return l;
    })
    .join("\n");
};

const buildHtml = (g: GuestPost) => {
  const lines = [`<h1>${g.h1}</h1>`, `<p>${g.intro}</p>`];
  for (const raw of g.content.split("\n")) {
    const t = raw.trim();
    if (!t) continue;
    if (t.startsWith("### ")) lines.push(`<h3>${t.slice(4)}</h3>`);
    else if (t.startsWith("## ")) lines.push(`<h2>${t.slice(3)}</h2>`);
    else lines.push(`<p>${t}</p>`);
  }
  return lines.join("\n");
};

const CopyBtn = ({ text, label }: { text: string; label: string }) => {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
      {done ? "Kopiert" : label}
    </Button>
  );
};

const GuestPostCard = ({ g }: { g: GuestPost }) => {
  const [tab, setTab] = useState<"markdown" | "html" | "plain">("markdown");
  const md = buildMarkdown(g);
  const html = buildHtml(g);
  const plain = buildPlainText(g);
  const current = tab === "markdown" ? md : tab === "html" ? html : plain;

  return (
    <article className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <header className="mb-4 flex flex-col gap-3 border-b border-border pb-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            ID {String(g.id).padStart(2, "0")} · {g.topic}
          </div>
          <h2 className="mt-1 text-xl font-bold leading-snug text-foreground">{g.h1}</h2>
          <div className="mt-1 text-sm text-muted-foreground">
            Primary Keyword: <span className="font-medium text-foreground">{g.primaryKeyword}</span> · Slug:{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{g.slug}</code>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <CopyBtn text={md} label="Markdown" />
          <CopyBtn text={html} label="HTML" />
          <CopyBtn text={plain} label="Plain" />
        </div>
      </header>

      <div className="mb-3 flex gap-1 rounded-md bg-muted p-1 text-xs font-medium w-fit">
        {(["markdown", "html", "plain"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded px-3 py-1 transition ${
              tab === t ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 p-4 text-sm leading-relaxed text-foreground">
{current}
      </pre>
    </article>
  );
};

const GuestPosts = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pb-20 pt-32 md:pt-40">
        <div className="container mx-auto px-6">
          <header className="mx-auto mb-10 max-w-3xl text-center">
            <h1 className="text-3xl font-bold text-foreground md:text-4xl">Guest Posts Dashboard</h1>
            <p className="mt-3 text-muted-foreground">
              {guestPosts.length} fertig formatierte Gastbeiträge zum Kopieren in externe Portale.
              Wähle Markdown, HTML oder reinen Text.
            </p>
          </header>

          <nav className="mx-auto mb-10 max-w-5xl rounded-lg border border-border bg-muted/30 p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Übersicht
            </div>
            <ul className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2 md:grid-cols-3">
              {guestPosts.map((g) => (
                <li key={g.id}>
                  <a href={`#gp-${g.id}`} className="text-foreground hover:underline">
                    ID {String(g.id).padStart(2, "0")} – {g.topic}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mx-auto grid max-w-5xl gap-8">
            {guestPosts.map((g) => (
              <div key={g.id} id={`gp-${g.id}`}>
                <GuestPostCard g={g} />
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GuestPosts;